import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import eventService from '../../services/eventService';
import useMediaQuery from '../../utils/useMediaQuery';
import PlanBar, { PlanSweep, PlanDismissedLine, PLAN_KEYFRAMES } from './plan/PlanBar';
import PlanSheet from './plan/PlanSheet';
import FindPlanPopover from './plan/FindPlanPopover';
import EventReviewSheet from './plan/EventReviewSheet';
import usePlanDetection from './plan/usePlanDetection';
import { highlightsFor, fromChatState } from './plan/planModel';
import PortalRing from '../stories/PortalRing';
import MediaViewer from '../MediaViewer';
import ringArt from '../../assets/img/portal-ring.png';
import messageService from '../../services/messageService';
import { onTyping } from '../../services/messagesRealtime';
import { mergeOutbox, isTempId } from '../../hooks/useOutbox';
import MessageBubble from './MessageBubble';
import Composer from './Composer';
import VoicePlayer from './VoicePlayer';
import ViewOnceViewer from './ViewOnceViewer';
import AlbumViewer from './AlbumViewer';
import { VideoMessageViewer } from './VideoMessage';
import { mergeRanges } from './voiceNoteModel';
import { T, RADIUS, groupMessages, seenReceipt, firstNameOf } from './messagesModel';
import { animateSent, animateIncoming, popSendRing, pulse } from './messagesMotion';
import { LockIcon } from './SecureLockSheet';
import ChatInfoSheet, { DOCK_MIN_WIDTH } from './ChatInfoSheet';
import { CallIcon } from './CallRecordCard';
import { fillFor } from '../wall/wallTokens';
import useContainerWidth from '../../hooks/useContainerWidth';

/**
 * The thread pane (handoff: "Thread pane"): header, the messages scroller with day pills
 * and runs, the typing bubble, the reply bar and the composer. Owns the tray, the reply
 * state and the send / arrive animations; the page owns the data and the doorway.
 */
export default function ThreadPane({
  detail,
  loading,
  error,
  onBack,
  outbox,
  onPeerProfile,
  onRefresh,
  viewerUserId,
  ringFor,
  onOpenReel,
  onAddPeople,
  systemLines = [],
  onToast,
  isMobile = false,
  /** The positioned layer the send ring pops in (the page's frame). */
  motionLayerRef = null,
  /** Secure chats: the thread is behind the viewer's lock and the window has run out. */
  locked = false,
  onUnlock,
  /** Chat info: the peer's live reel (for THEIR PORTAL), the secure toggle, delete, block. */
  reelFor = null,
  onToggleSecure = null,
  onDeleteConversation = null,
  onBlocked = null,
  /** Calls (Part B): start one from the header, the sheet or a record. `(video, event)`. */
  onStartCall = null,
  callActive = null,          // null | 'voice' | 'video' — the header button that lights up
}) {
  const [threadParams, setThreadParams] = useSearchParams();
  const scrollRef = useRef(null);
  const sendButtonRef = useRef(null);
  const [peerTyping, setPeerTyping] = useState(false);
  const typingClearRef = useRef(null);
  const [tray, setTray] = useState(null);
  const [trayMore, setTrayMore] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [highlightMessageId, setHighlightMessageId] = useState(null);
  const [viewingIndex, setViewingIndex] = useState(null);
  // The docked voice note: which message, where it flew from, and what the viewer has heard
  // of every voice note in the thread (seeded from the load, kept live by the dock).
  const [voice, setVoice] = useState(null);
  const [voiceState, setVoiceState] = useState({ playing: false, at: 0 });
  const [listenedById, setListenedById] = useState({});
  const [noteCountById, setNoteCountById] = useState({});
  // Waveforms this browser measured for older notes (new ones carry their own).
  const [peaksById, setPeaksById] = useState({});
  const dockRef = useRef(null);
  // Voice-notes handoff (Part C): writing a note is the composer's job — `noteMode` swaps the
  // bar for the note field; the hidden input takes focus inside the NOTE tap so iOS raises the
  // keyboard and the real field only has to inherit it. The header collapses while a note
  // plays or is being written; the chevron on the dock brings it back until the next play.
  const [noteMode, setNoteMode] = useState(null);        // { stamp }
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState(null);
  const [headerPeek, setHeaderPeek] = useState(false);
  const [voiceSearch, setVoiceSearch] = useState({ query: '', scope: 'note', filter: 'all' });
  const [voiceSearchFocused, setVoiceSearchFocused] = useState(false);
  const noteTrapRef = useRef(null);

  const summary = detail?.summary;
  const threadId = summary?.id;
  const peer = summary?.peer;
  const canAddPeople = !!summary && !summary.eventId;
  const pending = outbox.forThread(threadId);
  // Chat info (Oct 2026): docked beside the thread from 860px of pane width, an overlay below.
  const rootRef = useRef(null);
  const paneWidth = useContainerWidth(rootRef);
  const [infoOpen, setInfoOpen] = useState(false);
  const infoDocked = paneWidth >= DOCK_MIN_WIDTH;
  useEffect(() => { setInfoOpen(false); }, [threadId]);

  // ── Plan detection (Oct 2026, design_handoff_plan_detection) ──
  // One private suggestion for this chat: the bar above the composer, the dotted highlights on
  // the messages it read, the plan sheet, FIND A PLAN, and the published card. Never on a Secure chat.
  const navigate = useNavigate();
  const planPhone = useMediaQuery('(max-width: 639px)');
  const plan = usePlanDetection({ threadId, isSecure: !!summary?.isSecure, locked, onToast });
  const [planSheet, setPlanSheet] = useState(null);     // 'plan' | 'event' | null
  const [findOpen, setFindOpen] = useState(false);
  const [typingText, setTypingText] = useState(false);
  const planHighlights = useMemo(() => highlightsFor(plan.suggested ? plan.draft : null), [plan.draft, plan.suggested]);
  useEffect(() => { setPlanSheet(null); setFindOpen(false); }, [threadId]);
  const openPlanSheet = useCallback(() => { if (plan.draft) setPlanSheet('plan'); }, [plan.draft]);
  const planToPortal = useCallback(() => {
    if (!plan.draft) return;
    let cityFallback = null;
    try {
      const loc = JSON.parse(localStorage.getItem('pr.userLocation') || 'null');
      if (loc?.label) cityFallback = { label: loc.label, lat: loc.lat ?? null, lng: loc.lng ?? null };
    } catch { /* no stored location */ }
    const fromChat = fromChatState(plan.draft, viewerUserId, { cityFallback });
    setPlanSheet(null);
    navigate('/portals/new', { state: { fromChat } });
  }, [plan.draft, viewerUserId, navigate]);
  const planEventPublished = useCallback(async (eventId) => {
    setPlanSheet(null);
    if (!eventId || !threadId) return;
    const invitees = (plan.draft?.participants || []).filter((p) => p.userId !== viewerUserId && !p.isViewer).map((p) => p.userId);
    await Promise.all(invitees.map((uid) => eventService.inviteFriend(eventId, uid).catch(() => null)));
    const r = await messageService.planPublished(threadId, 'event', eventId);
    if (r.success && r.data) { plan.published('event', eventId); onRefresh?.(); } else onToast?.('EVENT MADE · THE CARD DIDN’T POST');
  }, [threadId, plan, viewerUserId, onRefresh, onToast]);
  const planAllowed = !!summary && !summary.isSecure && !locked;

  const closeTray = useCallback(() => { setTray(null); setTrayMore(false); }, []);
  const openTray = useCallback((id) => { setTray(id); setTrayMore(false); }, []);

  // Tray, reply, typing and the docked voice note all belong to one thread.
  useEffect(() => { closeTray(); setReplyTo(null); setPeerTyping(false); setVoice(null); setListenedById({}); setNoteCountById({}); setPeaksById({}); setNoteMode(null); setVoiceSearch({ query: '', scope: 'note', filter: 'all' }); setHeaderPeek(false); }, [threadId, closeTray]);

  // A thread load carries each voice note's heard ranges; the dock's live copy wins.
  useEffect(() => {
    const msgs = detail?.messages || [];
    setListenedById((prev) => {
      let next = prev;
      msgs.forEach((m) => {
        if (m.kind !== 'audio' || !m.audioListenedRanges || prev[m.id]) return;
        if (next === prev) next = { ...prev };
        next[m.id] = mergeRanges(m.audioListenedRanges, m.audioDurationSec || 0);
      });
      return next;
    });
  }, [detail?.messages]);
  // The docked note was unsent under us: the dock goes with it.
  useEffect(() => {
    if (!voice || !detail?.messages) return;
    if (voice.msg._outbox) return;
    if (!detail.messages.some((m) => m.id === voice.msg.id)) setVoice(null);
  }, [detail?.messages, voice]);

  const voiceIdRef = useRef(null);
  voiceIdRef.current = voice?.msg.id || null;
  // Opening a different voice note from the thread clears the query in THIS NOTE scope only.
  const clearNoteScopedQuery = useCallback(() => setVoiceSearch((s) => (s.scope === 'note' && s.query ? { ...s, query: '' } : s)), []);
  const playVoice = useCallback((msg, fromRect, fromEl) => {
    // A second tap on the docked note is play / pause, not a re-open.
    if (voiceIdRef.current === msg.id) { dockRef.current?.toggle(); return; }
    clearNoteScopedQuery(); setNoteMode(null);
    setVoice({ msg, fromRect: fromRect || null, fromEl: fromEl || null });
  }, [clearNoteScopedQuery]);
  // The note badge: dock the note and open its notes straight away.
  const openVoiceNotes = useCallback((msg, fromRect, fromEl) => {
    if (voiceIdRef.current === msg.id) { dockRef.current?.open(); return; }
    clearNoteScopedQuery(); setNoteMode(null);
    setVoice({ msg, fromRect: fromRect || null, fromEl: fromEl || null, notesOpen: true });
  }, [clearNoteScopedQuery]);
  // A WHOLE CHAT result in another voice note: load that one in the dock at that second. The
  // query, scope and filter stay.
  const jumpToVoice = useCallback((messageId, atSec) => {
    const msg = (detail?.messages || []).find((m) => m.id === messageId);
    if (!msg) return;
    setNoteMode(null);
    setVoice({ msg, fromRect: null, fromEl: null, notesOpen: true, startAt: atSec });
  }, [detail?.messages]);
  // The info sheet's VOICE tab: the dock on the newest voice note, searching the whole chat.
  const searchVoiceNotes = useCallback(() => {
    const voices = (detail?.messages || []).filter((m) => m.kind === 'audio');
    const newest = voices[voices.length - 1];
    if (!newest) return;
    setVoiceSearch((s) => ({ ...s, scope: 'chat' }));
    if (voiceIdRef.current === newest.id) { dockRef.current?.focusSearch(); return; }
    setVoice({ msg: newest, fromRect: null, fromEl: null, notesOpen: true, searchFocus: true });
  }, [detail?.messages]);
  const onListened = useCallback((id, ranges) => setListenedById((prev) => ({ ...prev, [id]: ranges })), []);
  const onPeaks = useCallback((id, peaks) => setPeaksById((prev) => ({ ...prev, [id]: peaks })), []);
  const onNotesChanged = useCallback((id, count) => setNoteCountById((prev) => (prev[id] === count ? prev : { ...prev, [id]: count })), []);
  const closeVoice = useCallback(() => { setVoice(null); setNoteMode(null); setHeaderPeek(false); }, []);
  const seekVoice = useCallback((t) => { dockRef.current?.seek(t); dockRef.current?.play(); }, []);
  const onVoiceSearchChange = useCallback((patch) => setVoiceSearch((s) => ({ ...s, ...patch })), []);

  // NOTE: pin the second, raise the keyboard inside the tap, hand the field over to the composer.
  const startNote = useCallback((stamp) => {
    try { noteTrapRef.current?.focus({ preventScroll: true }); } catch { /* no field yet */ }
    setNoteError(null);
    setNoteMode({ stamp });
  }, []);
  const repinNote = useCallback(() => setNoteMode((n) => (n ? { stamp: Math.floor(voiceState.at) } : n)), [voiceState.at]);
  const cancelNote = useCallback(() => { setNoteMode(null); setNoteError(null); }, []);
  // The composer's switch decides how it is saved, so the toast says where the note landed.
  const saveNote = useCallback(async (body, shared = false) => {
    if (!noteMode || noteSaving) return;
    setNoteSaving(true);
    const r = await dockRef.current?.saveNote(noteMode.stamp, body, shared);
    setNoteSaving(false);
    if (r?.success) {
      setNoteMode(null);
      const them = firstNameOf(peer?.displayName) || 'THEM';
      onToast?.(!body ? 'BOOKMARK SAVED' : shared ? `NOTE SAVED · SHARED WITH ${them}` : 'NOTE SAVED · ONLY YOU');
    } else setNoteError((r?.error || 'Could not save that note.').toUpperCase());
  }, [noteMode, noteSaving, onToast, peer]);

  // The header collapses while a note plays, is being written, or its notes are searched; the
  // dock's chevron brings it back until the next play starts.
  useEffect(() => { if (voiceState.playing) setHeaderPeek(false); }, [voiceState.playing]);
  const headerHidden = !!voice && (voiceState.playing || !!noteMode || voiceSearchFocused) && !headerPeek;
  const keyboardUp = isMobile && (!!noteMode || voiceSearchFocused);

  const handleEditMessage = async (id, body) => {
    const r = await messageService.editMessage(id, body);
    if (r.success) onRefresh?.();
    return r;
  };
  const handleUnsendMessage = async (id) => {
    const r = await messageService.unsendMessage(id);
    if (r.success) onRefresh?.();
    return r;
  };
  const handleReactMessage = async (id, emoji) => messageService.react(id, emoji);

  // View once: the one look. The open call is what records it, so the content is asked for
  // on the tap and held only in this state until the viewer closes — then the thread is
  // re-read and the bubble reads OPENED (the hub usually beat us to it).
  const [viewOnce, setViewOnce] = useState(null);   // { msg: the revealed message }
  const [videoOpen, setVideoOpen] = useState(null);  // a video message, full screen with sound
  // The album viewer: which album, which item it opened on, and whether it was a view-once
  // look (closing one re-reads the thread so the capsule reads OPENED).
  const [album, setAlbum] = useState(null);          // { msg, index, sealed }
  const openViewOnce = useCallback(async (msg) => {
    const r = await messageService.openViewOnce(msg.id);
    if (!r.success) { onToast?.((r.error || 'THAT DIDN’T WORK').toUpperCase()); onRefresh?.(); return; }
    // A sealed album opens as the album viewer — the one look is the whole reel.
    if (msg.kind === 'album') { setAlbum({ msg: { ...msg, ...r.data }, index: 0, sealed: true }); return; }
    setViewOnce({ msg: { ...msg, ...r.data } });
  }, [onToast, onRefresh]);
  const openAlbum = useCallback((msg, index = 0) => setAlbum({ msg, index, sealed: false }), []);
  const closeAlbum = useCallback(() => { setAlbum((a) => { if (a?.sealed) onRefresh?.(); return null; }); }, [onRefresh]);
  const closeViewOnce = useCallback(() => { setViewOnce(null); onRefresh?.(); }, [onRefresh]);
  const viewOnceId = viewOnce?.msg.id;
  const reportScreenshot = useCallback(() => { if (viewOnceId) messageService.reportViewOnceScreenshot(viewOnceId); }, [viewOnceId]);

  // ?message=<id> — land on the tagged message and flash it, rather than the bottom.
  useEffect(() => {
    const wanted = threadParams.get('message');
    if (!wanted || !detail?.messages?.length) return undefined;
    if (!detail.messages.some((m) => m.id === wanted)) return undefined;
    setHighlightMessageId(wanted);
    const jump = requestAnimationFrame(() => {
      document.getElementById(`msg-${wanted}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
    setThreadParams((p) => { p.delete('message'); return p; }, { replace: true });
    const clear = setTimeout(() => setHighlightMessageId(null), 2600);
    return () => { cancelAnimationFrame(jump); clearTimeout(clear); };
  }, [threadParams, detail?.messages, setThreadParams]);

  // Always pin the thread to the newest message; re-run as images load and fonts settle.
  const msgCount = (detail?.messages?.length || 0) + pending.length + systemLines.length;
  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);
  useEffect(() => {
    if (highlightMessageId) return undefined;
    scrollToBottom();
    const raf = requestAnimationFrame(() => { scrollToBottom(); requestAnimationFrame(scrollToBottom); });
    const settle = setTimeout(scrollToBottom, 160);
    const el = scrollRef.current;
    const imgs = el ? Array.from(el.querySelectorAll('img')) : [];
    const onImgLoad = () => scrollToBottom();
    imgs.forEach((img) => { if (!img.complete) img.addEventListener('load', onImgLoad); });
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(settle);
      imgs.forEach((img) => img.removeEventListener('load', onImgLoad));
    };
  }, [threadId, msgCount, peerTyping, replyTo, scrollToBottom, highlightMessageId]);

  // Typing events scoped to THIS thread, ignoring self.
  useEffect(() => {
    if (!threadId) return undefined;
    const peerId = peer?.userId;
    const off = onTyping((info) => {
      if (info?.threadId !== threadId) return;
      if (info?.userId === viewerUserId) return;
      if (peerId && !summary?.isGroup && info?.userId !== peerId) return;
      setPeerTyping(true);
      if (typingClearRef.current) clearTimeout(typingClearRef.current);
      typingClearRef.current = setTimeout(() => setPeerTyping(false), 3000);
    });
    return () => {
      off();
      if (typingClearRef.current) clearTimeout(typingClearRef.current);
      setPeerTyping(false);
    };
  }, [threadId, peer?.userId, summary?.isGroup, viewerUserId]);

  // ── Arrivals: which bubbles are new since the last commit, and whose they are. ──
  const seenIds = useRef(new Set());
  const seenThread = useRef(null);
  const sentTempIds = useRef(new Set());
  const merged = detail ? mergeOutbox(detail.messages, pending) : [];
  useLayoutEffect(() => {
    if (!threadId) return;
    const ids = merged.map((m) => m.id);
    if (seenThread.current !== threadId) {
      seenThread.current = threadId;
      seenIds.current = new Set(ids);
      return;
    }
    const fresh = merged.filter((m) => !seenIds.current.has(m.id));
    ids.forEach((id) => seenIds.current.add(id));
    fresh.forEach((m) => {
      const row = scrollRef.current?.querySelector(`[data-mid="${CSS.escape(m.id)}"]`);
      const bubble = row?.querySelector('[data-bubble]') || row;
      if (!bubble) return;
      if (isTempId(m.id) && sentTempIds.current.has(m.id)) {
        sentTempIds.current.delete(m.id);
        popSendRing({ layer: motionLayerRef?.current, button: sendButtonRef.current });
        animateSent({ bubble, button: sendButtonRef.current });
      } else if (!isTempId(m.id) && m.senderId !== viewerUserId && !m.system) {
        animateIncoming(bubble);
      }
    });
  });

  const send = (draft) => {
    const tempId = outbox.enqueue(draft);
    if (tempId) sentTempIds.current.add(tempId);
    return tempId;
  };

  const jumpTo = (id) => {
    const sc = scrollRef.current;
    const el = sc?.querySelector(`[data-mid="${CSS.escape(id)}"]`);
    if (!el || !sc) return;
    sc.scrollTop += el.getBoundingClientRect().top - sc.getBoundingClientRect().top - 80;
    pulse(el.querySelector('[data-bubble]') || el);
  };

  if (locked) {
    return (
      <div data-secure-locked className="h-full flex flex-col" style={{ background: T.canvas }}>
        {isMobile && (
          <div className="flex-shrink-0 flex items-center" style={{ background: T.white, padding: 'calc(12px + env(safe-area-inset-top, 0px)) 16px 12px', minHeight: 60 }}>
            <button type="button" onClick={onBack} className="font-mono font-bold" style={{ fontSize: 11, letterSpacing: '.1em', padding: '0 2px' }}>← BACK</button>
          </div>
        )}
        <div className="flex-1 flex flex-col items-center justify-center" style={{ gap: 16 }}>
          <span style={{ width: 64, height: 64, borderRadius: '50%', background: T.ink, color: T.white, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LockIcon size={24} />
          </span>
          <span className="font-mono font-bold" style={{ fontSize: 12, letterSpacing: '.12em' }}>SECURE CHAT LOCKED</span>
          <button type="button" onClick={onUnlock} className="font-mono font-bold hover:opacity-80" style={{ height: 40, padding: '0 22px', borderRadius: RADIUS.pill, background: T.ink, color: T.white, border: 0, fontSize: 11, letterSpacing: '.12em', cursor: 'pointer' }}>
            [ UNLOCK ]
          </button>
        </div>
      </div>
    );
  }

  if (loading || !detail) {
    return (
      <div className="h-full flex flex-col" style={{ background: T.canvas }}>
        {/* The header keeps its place while the thread loads, so BACK is never lost — a
            failed load on a phone otherwise had no way out but the browser. */}
        {isMobile && (
          <div className="flex-shrink-0 flex items-center" style={{ background: T.white, padding: '12px 16px', minHeight: 60 }}>
            <button type="button" onClick={onBack} className="font-mono font-bold" style={{ fontSize: 11, letterSpacing: '.1em', padding: '0 2px' }}>← BACK</button>
          </div>
        )}
        <div className="flex-1 flex flex-col items-center justify-center" style={{ gap: 14 }}>
          {loading ? (
            <span className="pr-portal-ring block" style={{ width: 56, height: 56, backgroundImage: `url(${ringArt})`, animationDuration: '1.2s' }} aria-label="Loading" />
          ) : (
            <>
              <span className="font-mono font-bold" style={{ fontSize: 12, letterSpacing: '.1em' }}>COULDN'T OPEN THAT.</span>
              <span className="font-mono text-center" style={{ fontSize: 11, letterSpacing: '.1em', color: T.grey500, padding: '0 24px' }}>{error || 'Check your connection and try again.'}</span>
              <button
                type="button"
                onClick={onRefresh}
                className="font-mono font-bold hover:bg-black hover:text-white"
                style={{ marginTop: 4, borderRadius: RADIUS.pill, border: `2px solid ${T.ink}`, background: 'transparent', fontSize: 11, letterSpacing: '.1em', padding: '10px 14px', transition: 'background-color 200ms, color 200ms' }}
              >
                RETRY
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  const photos = detail.messages.filter((m) => m.kind === 'image' && m.imageUrl);
  const openPhoto = (msg) => {
    const at = photos.findIndex((m) => m.id === msg.id);
    setViewingIndex(at >= 0 ? at : null);
  };

  const all = [...merged, ...systemLines].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  const groups = groupMessages(all, { viewerUserId, peerUserId: peer?.userId });
  const seen = seenReceipt(all, detail.reads, viewerUserId, { isGroup: !!summary?.isGroup });
  const headerRing = !summary?.isGroup && peer?.userId ? ringFor?.(peer.userId) : null;
  const replyingName = replyTo ? replyTo.name : null;

  const sheet = infoOpen && summary && (
    <>
      {!infoDocked && <div aria-hidden onClick={() => setInfoOpen(false)} style={{ position: 'absolute', inset: 0, zIndex: 40, background: 'rgba(0,0,0,.35)' }} />}
      <ChatInfoSheet
        threadId={threadId}
        peer={peer}
        isGroup={!!summary.isGroup}
        isSecure={!!summary.isSecure}
        viewerUserId={viewerUserId}
        messages={detail.messages}
        docked={infoDocked}
        plan={plan.suggested ? plan.draft : null}
        reel={peer?.userId ? reelFor?.(peer.userId) || null : null}
        onClose={() => setInfoOpen(false)}
        onOpenProfile={(href) => (href ? onPeerProfile?.(href) : null)}
        onJumpTo={jumpTo}
        onOpenPlanPortal={planToPortal}
        planAllowed={planAllowed}
        planEnabled={plan.chatEnabled}
        onTogglePlan={plan.setChat}
        onFindPlan={() => setFindOpen(true)}
        onOpenAlbum={(group, index) => openAlbum({ id: group.messageId, kind: 'album', senderId: group.senderId, createdAt: group.createdAt, albumItems: group.items, albumCount: group.items.length, senderDisplayName: group.senderId === viewerUserId ? 'You' : peer?.displayName }, index)}
        onOpenMessageMedia={(m, index) => { if (m.kind === 'album') openAlbum(m, index); else if (m.kind === 'video') setVideoOpen(m); else openPhoto(m); }}
        onSearchVoiceNotes={() => { searchVoiceNotes(); if (!infoDocked) setInfoOpen(false); }}
        onOpenReel={onOpenReel}
        onToggleSecure={onToggleSecure && !summary.isGroup ? (next) => onToggleSecure(threadId, next) : null}
        onAddPeople={canAddPeople ? onAddPeople : null}
        onStartCall={onStartCall}
        onDeleteConversation={onDeleteConversation ? () => onDeleteConversation(threadId) : null}
        onBlocked={onBlocked}
        onToast={onToast}
      />
    </>
  );

  return (
    <div ref={rootRef} className="h-full min-h-0 flex relative" style={{ background: T.canvas }}>
    {/* data-call-anchor: on desktop the full call screen fills this column, not the viewport. */}
    <div data-call-anchor={isMobile ? undefined : 'true'} className="h-full min-h-0 flex-1 min-w-0 flex flex-col" style={{ background: T.canvas }}>
      {/* The header (handoff §5, Part B's icons): back · identity (opens chat info) · voice ·
          video · info. On a phone this IS the top of the screen (the app bar steps out while a
          thread is open — see MessagesPage), so it takes the notch inset itself. */}
      <div data-testid="thread-header-shell" data-collapsed={headerHidden || undefined} className="flex-shrink-0" style={{ overflow: 'hidden', maxHeight: headerHidden ? 0 : 160, transition: 'max-height 200ms linear' }}>
      <div data-testid="thread-header" className="flex-shrink-0 flex items-center" style={{ background: T.white, minHeight: 64, padding: isMobile ? 'calc(8px + env(safe-area-inset-top, 0px)) 12px 8px 16px' : '0 12px 0 16px', gap: 12 }}>
        {isMobile && (
          <button type="button" onClick={onBack} aria-label="Back" style={{ width: 36, height: 36, border: 0, padding: 0, background: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginLeft: -6, cursor: 'pointer' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="square"><path d="M19 12H5M11 6l-6 6 6 6" /></svg>
          </button>
        )}
        {headerRing ? (
          <PortalRing
            {...headerRing}
            size={40}
            onClick={(e) => onOpenReel?.(peer.userId, e.portalRingRect || e.currentTarget?.getBoundingClientRect?.())}
            ariaLabel={`Portal from ${peer?.displayName}`}
          />
        ) : (
          <button
            type="button"
            onClick={() => setInfoOpen((o) => !o)}
            aria-label="Chat info"
            className="flex items-center justify-center overflow-hidden font-mono font-bold text-black flex-shrink-0"
            style={{ width: 40, height: 40, borderRadius: RADIUS.thumb, background: T.activeBadge, fontSize: 14, border: 0, padding: 0, cursor: 'pointer' }}
          >
            {peer?.avatarUrl ? <img src={peer.avatarUrl} alt="" className="w-full h-full object-cover" /> : (peer?.displayName || '?').charAt(0).toUpperCase()}
          </button>
        )}
        <button
          type="button"
          data-testid="thread-identity"
          onClick={() => setInfoOpen((o) => !o)}
          className="flex-1 min-w-0 flex flex-col text-left"
          style={{ gap: 3, border: 0, padding: 0, background: 'none', cursor: 'pointer' }}
        >
          <span className="flex items-center min-w-0" style={{ gap: 6 }}>
            {summary?.isSecure && <span data-secure-pill style={{ display: 'flex', flexShrink: 0 }}><LockIcon size={12} strokeWidth={2.5} /></span>}
            <span className="font-mono font-bold uppercase truncate" style={{ fontSize: 14, letterSpacing: '.08em' }}>{peer?.displayName}</span>
          </span>
          <span className="font-mono truncate uppercase" style={{ fontSize: 10, letterSpacing: '.1em', color: T.grey500 }}>
            {[
              summary?.isSecure && 'SECURE',
              summary?.kind === 'host' && 'HOST THREAD',
              summary?.allowsAttendeeReplies === false && 'BROADCAST · NO REPLIES',
              summary?.eventTitle ? `IN: ${summary.eventTitle}` : peer?.subLabel ? `WITH ${peer.subLabel}` : peer?.handle ? `@${peer.handle}` : null,
            ].filter(Boolean).join(' · ')}
          </span>
        </button>
        <div className="flex flex-shrink-0" style={{ gap: 6 }}>
          {!summary?.isGroup && peer?.userId && (
            <>
              <HeaderIconButton label="Voice call" on={callActive === 'voice'} onClick={(e) => onStartCall?.(false, e)} testId="thread-voice-call">
                <CallIcon name="phone" size={18} />
              </HeaderIconButton>
              <HeaderIconButton label="Video call" on={callActive === 'video'} onClick={(e) => onStartCall?.(true, e)} testId="thread-video-call">
                <CallIcon name="video" size={18} />
              </HeaderIconButton>
            </>
          )}
          <HeaderIconButton label="Chat info" on={infoOpen} onClick={() => setInfoOpen((o) => !o)} testId="thread-info" expanded={infoOpen}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="square"><circle cx="12" cy="12" r="9.5" /><path d="M12 11v6M12 7v1.5" /></svg>
          </HeaderIconButton>
        </div>
      </div>
      </div>
      {/* With the header away on a phone the dock takes the notch inset itself. */}
      {headerHidden && isMobile && <div aria-hidden style={{ height: 'env(safe-area-inset-top, 0px)', flexShrink: 0 }} />}

      {voice && (
        <VoicePlayer
          key={voice.msg.id}
          ref={dockRef}
          msg={voice.msg}
          threadId={threadId}
          fromRect={voice.fromRect}
          fromEl={voice.fromEl}
          listened={listenedById[voice.msg.id] || voice.msg.audioListenedRanges || []}
          onListened={onListened}
          onState={setVoiceState}
          onClose={closeVoice}
          notesOpenInitially={!!voice.notesOpen}
          onNotesChanged={onNotesChanged}
          peaks={peaksById[voice.msg.id] || voice.msg.audioPeaks || null}
          onPeaks={onPeaks}
          isMobile={isMobile}
          ephemeral={!!voice.msg._outbox}
          mine={voice.msg.senderId === viewerUserId}
          viewerUserId={viewerUserId}
          peerName={peer?.displayName || ''}
          startAt={voice.startAt ?? null}
          headerHidden={headerHidden}
          onShowHeader={() => setHeaderPeek(true)}
          noteMode={!!noteMode}
          onStartNote={startNote}
          search={voiceSearch}
          onSearchChange={onVoiceSearchChange}
          onSearchFocus={setVoiceSearchFocused}
          searchFocusInitially={!!voice.searchFocus}
          keyboardUp={keyboardUp}
          onJumpToVoice={jumpToVoice}
          onToast={onToast}
        />
      )}
      {/* The NOTE tap focuses this so iOS raises the keyboard; the composer's field inherits it. */}
      <input ref={noteTrapRef} aria-hidden tabIndex={-1} readOnly data-testid="note-focus-trap" style={{ position: 'fixed', left: -9999, top: 0, width: 1, height: 1, opacity: 0, fontSize: 16 }} />

      <div className="relative flex-1 min-h-0 flex flex-col">
      <style>{PLAN_KEYFRAMES}</style>
      <PlanSweep key={plan.arrival} play={plan.suggested && plan.arrival > 0} />
      <div
        ref={scrollRef}
        data-scroll="msgs"
        className="flex-1 min-h-0 overflow-y-auto"
        style={{ padding: isMobile ? '16px 16px' : '16px 20px' }}
        onClick={() => { if (tray) closeTray(); }}
      >
        {error && (
          <div className="mb-3 font-mono text-xs uppercase tracking-wider" style={{ padding: '10px 14px', borderRadius: RADIUS.row, background: T.white, color: T.danger }}>{error}</div>
        )}
        {groups.map((g) => (
          <div key={g.day}>
            <DayPill label={g.day} />
            {g.items.map((item) => (
              <MessageBubble
                key={item.msg.id}
                item={item}
                isGroup={summary?.isGroup}
                highlighted={item.msg.id === highlightMessageId}
                trayOpen={tray === item.msg.id}
                trayMore={tray === item.msg.id && trayMore}
                onOpenTray={openTray}
                onCloseTray={closeTray}
                onTrayMore={setTrayMore}
                onReply={setReplyTo}
                onCopy={() => onToast?.('COPIED')}
                onEdit={handleEditMessage}
                onUnsend={handleUnsendMessage}
                onReact={handleReactMessage}
                onRetry={outbox.retry}
                onDiscard={outbox.discard}
                onOpenImage={openPhoto}
                onOpenVideo={setVideoOpen}
                onOpenAlbum={openAlbum}
                onRetryItem={outbox.retryItem}
                onDropFailed={outbox.dropFailed}
                peerName={peer?.displayName}
                onCallBack={onStartCall ? (m, e) => onStartCall(!!m.call?.video, e) : null}
                onJumpTo={jumpTo}
                viewerUserId={viewerUserId}
                onOpenViewOnce={openViewOnce}
                seen={seen?.messageId === item.msg.id ? seen.label : null}
                voiceRanges={listenedById[item.msg.id]}
                voiceActive={voice?.msg.id === item.msg.id}
                voicePlaying={voice?.msg.id === item.msg.id && voiceState.playing}
                voiceAt={voice?.msg.id === item.msg.id ? voiceState.at : 0}
                onPlayVoice={playVoice}
                onSeekVoice={seekVoice}
                voiceNoteCount={noteCountById[item.msg.id] ?? item.msg.audioNoteCount ?? 0}
                onOpenVoiceNotes={openVoiceNotes}
                voicePeaks={peaksById[item.msg.id] || null}
                highlights={planHighlights.get(item.msg.id) || null}
                onOpenPlan={openPlanSheet}
                highlightDelay={180 + ((item.msg.id || '').charCodeAt(3) % 4) * 90}
              />
            ))}
          </div>
        ))}
        {peerTyping && <TypingBubble name={summary?.isGroup ? 'SOMEONE' : (peer?.displayName || 'PEER').toUpperCase()} />}
        {plan.dismissed && <PlanDismissedLine onUndo={plan.undo} />}
      </div>
      </div>

      {planAllowed && (
        <PlanBar draft={plan.suggested ? plan.draft : null} hidden={typingText} animateKey={plan.draft ? `${plan.draft.id}:${plan.arrival}` : null} onReview={openPlanSheet} onDismiss={plan.dismiss} />
      )}

      {summary?.allowsAttendeeReplies === false && !summary?.viewerIsHost ? (
        <div className="flex-shrink-0 text-center" style={{ background: T.white, padding: 16 }}>
          <div className="font-mono font-bold uppercase" style={{ fontSize: 10, letterSpacing: '.1em', color: T.grey600 }}>ANNOUNCEMENT THREAD</div>
          <p className="mt-1 font-mono text-xs" style={{ color: T.grey500 }}>Replies are turned off by the host on this broadcast.</p>
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <Composer
            threadId={threadId}
            onSend={send}
            members={detail.members || []}
            replyTo={replyTo && { ...replyTo, name: replyingName }}
            onCancelReply={() => setReplyTo(null)}
            sendButtonRef={sendButtonRef}
            onFindPlan={planAllowed ? () => setFindOpen((o) => !o) : null}
            findPlanActive={findOpen}
            onDraftChange={setTypingText}
            onToast={onToast}
            noteMode={noteMode && voice ? {
              stamp: noteMode.stamp,
              at: voiceState.at,
              senderName: voice.msg.senderDisplayName || peer?.displayName || '',
              peerName: peer?.displayName || '',
              mine: voice.msg.senderId === viewerUserId,
              onRepin: repinNote,
              onSave: saveNote,
              onCancel: cancelNote,
              saving: noteSaving,
              error: noteError,
              // A phone's note field floats over the thread and the dock, on the keyboard.
              overlay: isMobile,
            } : null}
          />
          <FindPlanPopover open={findOpen} onClose={() => setFindOpen(false)} onRun={plan.find} onStartAnyway={() => navigate('/portals/new', { state: { fromChat: { threadId, threadName: summary?.peer?.displayName || '', text: '', city: null, venue: null, venueOpen: false, window: null, aud: ['friends'], joinMode: 'open', clubIds: [], inviteUserIds: (detail.members || []).map((m) => m.userId), people: (detail.members || []).map((m) => ({ userId: m.userId, name: m.displayName, avatarUrl: m.avatarUrl || null })), sources: [] } } })} />
        </div>
      )}

      {planSheet === 'plan' && (
        <PlanSheet draft={plan.draft} viewerUserId={viewerUserId} mobile={planPhone} onClose={() => setPlanSheet(null)} onPortal={planToPortal} onEvent={() => setPlanSheet('event')} />
      )}
      {planSheet === 'event' && (
        <EventReviewSheet draft={plan.draft} mobile={planPhone} onClose={() => setPlanSheet(null)} onPortal={() => setPlanSheet('plan')} onPublished={planEventPublished} />
      )}

      {videoOpen && <VideoMessageViewer msg={videoOpen} onClose={() => setVideoOpen(null)} />}
      {album && (
        <AlbumViewer
          msg={album.msg}
          index={album.index}
          mine={album.msg.senderId === viewerUserId}
          senderName={album.msg.senderDisplayName || peer?.displayName || ''}
          senderAvatarUrl={album.msg.senderAvatarUrl || null}
          onClose={closeAlbum}
          onToast={onToast}
          onReply={album.msg.senderId !== viewerUserId && !album.msg._outbox ? (body, at) => {
            const name = album.msg.senderDisplayName || peer?.displayName || '';
            const quote = { id: album.msg.id, senderDisplayName: name, bodySnippet: `[ ALBUM · ${at.total} ] ${at.item.kind === 'video' ? 'VIDEO' : 'PHOTO'} ${at.index + 1} OF ${at.total}` };
            send({ threadId, kind: 'text', payload: { kind: 'text', body, replyToMessageId: album.msg.id, quote } });
          } : null}
        />
      )}
      {viewOnce && (
        <ViewOnceViewer
          msg={viewOnce.msg}
          senderName={viewOnce.msg.senderDisplayName}
          onClose={closeViewOnce}
          onScreenshot={reportScreenshot}
        />
      )}

      {viewingIndex !== null && (
        <MediaViewer
          items={photos.map((m) => ({ kind: 'image', url: m.imageUrl, thumbnailUrl: m.imageUrl }))}
          index={viewingIndex}
          onIndex={setViewingIndex}
          onClose={() => setViewingIndex(null)}
          title={peer?.displayName || ''}
        />
      )}
    </div>
    {sheet}
    </div>
  );
}

/** One of the header's 40px round icon buttons: grey, inverts on hover, ON state in the ring. */
function HeaderIconButton({ label, on = false, onClick, children, testId, expanded }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      aria-pressed={expanded === undefined ? undefined : expanded}
      data-testid={testId}
      className={on ? '' : 'hover:bg-black hover:text-white'}
      style={{ width: 40, height: 40, borderRadius: '50%', border: '2px solid transparent', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: on ? fillFor('on') : T.off, color: on ? T.white : T.ink, cursor: 'pointer', transition: 'color 200ms, background-color 200ms' }}
    >
      {children}
    </button>
  );
}

/** A centred day marker: a grey pill, no rules. */
export function DayPill({ label }) {
  return (
    <div className="flex items-center justify-center" style={{ margin: '16px 0' }}>
      <span className="font-mono font-bold" style={{ fontSize: 9, letterSpacing: '.1em', color: T.grey600, background: T.dayPill, padding: '4px 10px', borderRadius: RADIUS.pill }}>{label}</span>
    </div>
  );
}

/** "{NAME} IS TYPING…": a white bubble with a spinning ring and three round dots. */
export function TypingBubble({ name }) {
  return (
    <div data-typing className="flex justify-start" style={{ margin: '4px 0 8px' }}>
      <div className="flex items-center" style={{ background: T.white, borderRadius: RADIUS.bubble, padding: '8px 12px', gap: 8 }}>
        <span className="pr-portal-ring block flex-shrink-0" style={{ width: 18, height: 18, backgroundImage: `url(${ringArt})`, animationDuration: '1.2s' }} aria-hidden />
        <span className="flex items-end" style={{ gap: 4 }} aria-label="Typing">
          {[0, 150, 300].map((delay) => (
            <span key={delay} className="pr-typing-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: T.ink, animationDelay: `${delay}ms` }} />
          ))}
        </span>
        <span className="font-mono font-bold" style={{ fontSize: 10, letterSpacing: '.1em', color: T.grey500 }}>{name} IS TYPING…</span>
      </div>
    </div>
  );
}

/** Desktop, no thread: a spinning ring and a way in. */
export function EmptyPane({ onNewMessage }) {
  return (
    <div className="flex-1 h-full flex flex-col items-center justify-center" style={{ gap: 14, color: T.ink, background: T.canvas }}>
      <span className="relative block" style={{ width: 112, height: 112, marginBottom: 6 }}>
        <span className="pr-portal-ring absolute inset-0" style={{ backgroundImage: `url(${ringArt})` }} aria-hidden />
        <span className="absolute rounded-full" style={{ inset: 6, background: T.canvas }} aria-hidden />
      </span>
      <span className="font-mono font-bold" style={{ fontSize: 12, letterSpacing: '.1em' }}>SELECT A THREAD</span>
      <span className="font-mono" style={{ fontSize: 11, letterSpacing: '.1em', color: T.grey500 }}>OR OPEN A NEW ONE.</span>
      <button
        type="button"
        onClick={(e) => onNewMessage?.(e.currentTarget.getBoundingClientRect())}
        className="font-mono font-bold hover:bg-black hover:text-white"
        style={{ marginTop: 4, borderRadius: RADIUS.pill, border: `2px solid ${T.ink}`, background: 'transparent', fontSize: 11, letterSpacing: '.1em', padding: '10px 14px', transition: 'background-color 200ms, color 200ms' }}
      >
        + NEW MESSAGE
      </button>
    </div>
  );
}
